import React from 'react';
import { Mail, UserPlus, Link2, Upload, ArrowRight, LogIn } from 'lucide-react';

// Step-by-step onboarding guide shown to logged-out B2B visitors so they
// know exactly how to set up their trade portal access. Mirrors the real
// invite → register → link → upload flow.
const STEPS = [
  {
    icon: Mail,
    title: 'Check your invite email',
    body: 'We email an invitation to your registered work address. Open it and click the activation button inside.',
  },
  {
    icon: UserPlus,
    title: 'Register your account',
    body: 'Use the same work email the invite was sent to so we can match it to your trade account automatically.',
  },
  {
    icon: Link2,
    title: 'Account links instantly',
    body: 'On first sign-in your portal access is linked to our trade records — no manual setup needed.',
  },
  {
    icon: Upload,
    title: 'Upload a purchase order',
    body: 'Drop your PO PDF into the portal and we read it, allocate stock and email your invoice.',
  },
  {
    icon: LogIn,
    title: 'Next time, just sign in',
    body: 'Once registered, simply sign straight in to upload orders and track their status anytime.',
  },
];

export default function GettingStarted({ onSignIn }) {
  return (
    <div className="space-y-8">
      <div className="border border-primary/20 bg-primary/[0.03] p-6 sm:p-8">
        <p className="text-primary text-xs font-bold tracking-widest mb-2">GETTING STARTED</p>
        <h2 className="text-white font-black text-xl sm:text-2xl uppercase mb-2">How to access your trade portal</h2>
        <p className="text-white/50 text-sm leading-relaxed max-w-2xl">
          New to the Motion Reality trade portal? Follow these steps to activate your account and place your first order.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          return (
            <div key={idx} className="border border-white/10 bg-card p-5 flex gap-4">
              <div className="shrink-0 w-10 h-10 flex items-center justify-center bg-primary/10 border border-primary/20">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white/30 text-[10px] font-bold tracking-widest">STEP {idx + 1}</span>
                </div>
                <p className="text-white font-bold text-sm mb-1">{step.title}</p>
                <p className="text-white/40 text-xs leading-relaxed">{step.body}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-white/10 bg-card p-6 sm:p-8 text-center">
        <p className="text-white/50 text-sm mb-4 max-w-md mx-auto leading-relaxed">
          Already registered? Sign in to upload a purchase order or check your order status.
        </p>
        <button
          onClick={onSignIn}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest px-8 py-3.5 transition-colors"
        >
          SIGN IN <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-white/30 text-xs mt-4">
          Don't have a trade account yet?{' '}
          <a href="/b2b/apply" className="text-primary hover:underline font-bold">Apply here</a>
        </p>
      </div>
    </div>
  );
}